import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  BookOpen, ArrowLeft, Check, X, Sparkles, 
  Trophy, Star, RefreshCw, HelpCircle, Loader2, Laugh
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuizPage = ({ user }) => {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(5);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [quizComplete, setQuizComplete] = useState(false);
  const [notion, setNotion] = useState("");
  const [availableNotions, setAvailableNotions] = useState([]);
  const [encouragement, setEncouragement] = useState("");

  const notions = [
    { id: "art", name: "L'art", icon: "🎨" },
    { id: "bonheur", name: "Le bonheur", icon: "😊" },
    { id: "conscience", name: "La conscience", icon: "🧠" },
    { id: "devoir", name: "Le devoir", icon: "⚖️" },
    { id: "etat", name: "L'État", icon: "🏛️" },
    { id: "inconscient", name: "L'inconscient", icon: "🌙" },
    { id: "justice", name: "La justice", icon: "⚖️" },
    { id: "langage", name: "Le langage", icon: "💬" },
    { id: "liberte", name: "La liberté", icon: "🕊️" },
    { id: "nature", name: "La nature", icon: "🌿" },
    { id: "raison", name: "La raison", icon: "💡" },
    { id: "religion", name: "La religion", icon: "✝️" },
    { id: "science", name: "La science", icon: "🔬" },
    { id: "technique", name: "La technique", icon: "⚙️" },
    { id: "temps", name: "Le temps", icon: "⏰" },
    { id: "travail", name: "Le travail", icon: "🛠️" },
    { id: "verite", name: "La vérité", icon: "🔍" }
  ];

  useEffect(() => {
    setAvailableNotions(notions);
    setLoading(false);
  }, []);

  const startQuiz = async (selectedNotion) => {
    setNotion(selectedNotion);
    setLoading(true);
    setQuizStarted(true);
    setScore(0);
    setQuestionIndex(0);
    setQuizComplete(false);
    
    try {
      await fetchQuestion(selectedNotion, 0);
    } catch (error) {
      console.error("Error starting quiz:", error);
      toast.error("Erreur lors du chargement du quiz");
      setQuizStarted(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestion = async (notionId, index) => {
    setLoading(true);
    setShowResult(false);
    setSelectedAnswer(null);
    
    try {
      const response = await axios.post(`${API}/quiz/generate`, {
        notion: notionId,
        student_id: user?.id,
        question_index: index,
        difficulty: "remedial"
      });
      
      setCurrentQuestion(response.data);
      setEncouragement(response.data.encouragement || "");
    } catch (error) {
      console.error("Error fetching question:", error);
      toast.error("Erreur lors du chargement de la question");
    } finally {
      setLoading(false);
    }
  };

  const selectAnswer = (answerIndex) => {
    if (showResult) return;
    setSelectedAnswer(answerIndex);
  };

  const submitAnswer = async () => {
    if (selectedAnswer === null) return;
    
    const correct = selectedAnswer === currentQuestion.correct_index;
    setIsCorrect(correct);
    setShowResult(true);
    
    if (correct) {
      setScore(score + 1);
      toast.success("Bravo ! Bonne réponse ! 🎉");
    } else {
      toast.info("Pas tout à fait... Mais tu vas y arriver ! 💪");
    }
    
    // Track answer
    try {
      await axios.post(`${API}/quiz/remediation/answer`, {
        student_id: user?.id,
        notion: notion,
        question_id: currentQuestion.id,
        is_correct: correct
      });
    } catch (error) {
      console.error("Error tracking answer:", error);
    }
  };

  const nextQuestion = async () => {
    if (questionIndex >= totalQuestions - 1) {
      setQuizComplete(true);
      return;
    }
    
    const nextIndex = questionIndex + 1;
    setQuestionIndex(nextIndex);
    await fetchQuestion(notion, nextIndex);
  };

  const getAnswerStyle = (index) => {
    if (!showResult) {
      return selectedAnswer === index 
        ? "border-[#1A365D] bg-blue-50 ring-2 ring-[#1A365D]" 
        : "border-gray-200 hover:border-[#1A365D] hover:bg-gray-50";
    }
    
    if (index === currentQuestion.correct_index) {
      return "border-green-500 bg-green-50 ring-2 ring-green-500";
    }
    
    if (selectedAnswer === index && index !== currentQuestion.correct_index) {
      return "border-red-500 bg-red-50 ring-2 ring-red-500";
    }
    
    return "border-gray-200 opacity-50";
  };

  const renderNotionSelection = () => (
    <div className="max-w-3xl mx-auto" data-testid="notion-selection">
      <div className="text-center mb-8">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#22C55E] flex items-center justify-center">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-2xl font-medium text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Quiz de Remédiation
        </h2>
        <p className="text-gray-500">
          Choisissez une notion pour tester vos connaissances de base. 
          <br />
          <span className="text-[#22C55E] font-medium">Des réponses drôles vous attendent !</span>
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {availableNotions.map((n) => (
          <Card 
            key={n.id}
            className="cursor-pointer hover:shadow-lg hover:scale-105 transition-all"
            onClick={() => startQuiz(n.id)}
            data-testid={`notion-${n.id}`}
          >
            <CardContent className="p-4 text-center">
              <span className="text-3xl mb-2 block">{n.icon}</span>
              <p className="font-medium text-[#1A365D]">{n.name}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderQuestion = () => {
    if (!currentQuestion) return null;

    return (
      <div className="max-w-2xl mx-auto" data-testid="quiz-question">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">
              Question {questionIndex + 1} / {totalQuestions}
            </span>
            <Badge className="bg-[#22C55E]">
              <Star className="w-3 h-3 mr-1" />
              Score: {score}
            </Badge>
          </div>
          <Progress value={((questionIndex + 1) / totalQuestions) * 100} className="h-2" />
        </div>

        {/* Encouragement */}
        {encouragement && (
          <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-700">
            <Sparkles className="w-4 h-4 inline mr-1" />
            {encouragement}
          </div>
        )}

        {/* Question Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-xl text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
              {currentQuestion.question}
            </CardTitle>
            {currentQuestion.hint && !showResult && (
              <CardDescription className="flex items-center gap-1 text-[#D4AF37]">
                <HelpCircle className="w-4 h-4" />
                Indice : {currentQuestion.hint}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {currentQuestion.answers.map((answer, index) => (
              <div
                key={index}
                onClick={() => selectAnswer(index)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${getAnswerStyle(index)}`}
                data-testid={`answer-${index}`}
              >
                <div className="flex items-center justify-between">
                  <span className={showResult && index === currentQuestion.correct_index ? "font-medium" : ""}>
                    {answer.text}
                  </span>
                  {showResult && index === currentQuestion.correct_index && (
                    <Check className="w-5 h-5 text-green-500" />
                  )}
                  {showResult && selectedAnswer === index && index !== currentQuestion.correct_index && (
                    <X className="w-5 h-5 text-red-500" />
                  )}
                  {answer.is_absurd && showResult && (
                    <Laugh className="w-5 h-5 text-amber-500" />
                  )}
                </div>
                {answer.is_absurd && showResult && (
                  <p className="text-xs text-amber-600 mt-1 italic">
                    (Réponse absurde pour le fun !)
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Explanation after answer */}
        {showResult && currentQuestion.explanation && (
          <Card className="mb-6 border-[#1A365D]">
            <CardContent className="pt-4">
              <p className="text-sm text-[#1A365D]">
                <BookOpen className="w-4 h-4 inline mr-1" />
                <strong>Explication :</strong> {currentQuestion.explanation}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          {!showResult ? (
            <Button
              onClick={submitAnswer}
              disabled={selectedAnswer === null}
              className="flex-1 h-12 bg-[#1A365D] hover:bg-[#132745]"
              data-testid="submit-answer-btn"
            >
              Valider ma réponse
            </Button>
          ) : (
            <Button
              onClick={nextQuestion}
              className="flex-1 h-12 bg-[#22C55E] hover:bg-[#16a34a]"
              data-testid="next-question-btn"
            >
              {questionIndex >= totalQuestions - 1 ? "Voir mes résultats" : "Question suivante"}
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const percentage = Math.round((score / totalQuestions) * 100);
    let message = "";
    let emoji = "";
    
    if (percentage === 100) {
      message = "Parfait ! Tu maîtrises les bases !";
      emoji = "🏆";
    } else if (percentage >= 80) {
      message = "Excellent ! Tu es sur la bonne voie !";
      emoji = "🌟";
    } else if (percentage >= 60) {
      message = "Bien joué ! Continue comme ça !";
      emoji = "👍";
    } else if (percentage >= 40) {
      message = "C'est un bon début ! La philosophie demande de la pratique.";
      emoji = "💪";
    } else {
      message = "Ne te décourage pas ! Rome ne s'est pas faite en un jour.";
      emoji = "🌱";
    }

    return (
      <div className="max-w-md mx-auto text-center" data-testid="quiz-results">
        <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-[#22C55E] flex items-center justify-center">
          <Trophy className="w-12 h-12 text-white" />
        </div>
        
        <h2 className="text-3xl font-bold text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          {score} / {totalQuestions}
        </h2>
        
        <p className="text-5xl mb-4">{emoji}</p>
        
        <p className="text-lg text-gray-600 mb-6">{message}</p>
        
        <div className="p-4 bg-gray-50 rounded-lg mb-6">
          <p className="text-sm text-gray-500">
            Notion testée : <strong>{notions.find(n => n.id === notion)?.name}</strong>
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            onClick={() => startQuiz(notion)}
            className="w-full bg-[#1A365D] hover:bg-[#132745]"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Recommencer ce quiz
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setQuizStarted(false);
              setQuizComplete(false);
            }}
          >
            Choisir une autre notion
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
          >
            Retour au tableau de bord
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="quiz-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
          <span className="navbar-logo hidden sm:block">SophIA</span>
          <Badge className="ml-2 bg-[#22C55E] text-white">Quiz Remédiation</Badge>
        </div>
      </nav>

      <main className="px-4 md:px-8 py-6 page-enter">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#1A365D]" />
          </div>
        ) : !quizStarted ? (
          renderNotionSelection()
        ) : quizComplete ? (
          renderResults()
        ) : (
          renderQuestion()
        )}
      </main>
    </div>
  );
};

export default QuizPage;
